import { useEffect, useState, useMemo, useCallback } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  Search,
  Sparkles,
  Clock,
  AlertCircle,
  ShoppingBag,
  ChevronRight,
  CheckCircle,
} from 'lucide-react'
import { api, formatMoney, type CategoryTree, type ProductSummary } from '../api/client'
import { useTranslation } from '../i18n/LanguageContext'

// Category icon map for visual flair
const CATEGORY_ICONS: Record<string, string> = {
  vegetables: '🥬',
  fruits: '🍎',
  dairy: '🥛',
  bakery: '🍞',
  staples: '🌾',
  snacks: '🍿',
  beverages: '🧃',
  organic: '🌿',
  spices: '🌶️',
  meat: '🍗',
}

const CATEGORY_NAMES_HI: Record<string, string> = {
  vegetables: 'ताज़ी सब्जियां',
  fruits: 'ताज़े फल',
  dairy: 'दूध, दही और मक्खन',
  bakery: 'ब्रेड और बेकरी',
  staples: 'आटा, चावल और दालें',
  snacks: 'नमकीन और स्नैक्स',
  beverages: 'कोल्ड ड्रिंक और पेय',
  organic: 'ऑर्गेनिक उत्पाद',
  spices: 'मसाले और तेल',
  meat: 'अंडे और मांस',
}

function getCategoryEmoji(slug: string, name: string): string {
  const lower = `${slug} ${name}`.toLowerCase()
  for (const [key, emoji] of Object.entries(CATEGORY_ICONS)) {
    if (lower.includes(key)) return emoji
  }
  return '🛒'
}

function getLocalizedCategoryName(slug: string, name: string, isHindi: boolean): string {
  if (!isHindi) return name
  const lower = `${slug} ${name}`.toLowerCase()
  for (const [key, hiName] of Object.entries(CATEGORY_NAMES_HI)) {
    if (lower.includes(key)) return hiName
  }
  return name
}

export function Catalog() {
  const { categorySlug } = useParams<{ categorySlug?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const urlQuery = searchParams.get('q') || ''

  const { isHindi } = useTranslation()

  const [categories, setCategories] = useState<CategoryTree[]>([])
  const [products, setProducts] = useState<ProductSummary[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState(false)
  const [query, setQuery] = useState(urlQuery)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch categories once
  useEffect(() => {
    api.catalogue
      .categories()
      .then(setCategories)
      .catch(() => setCategories([]))
  }, [])

  const runSearch = useCallback(
    async (value: string) => {
      setQuery(value)
      if (value.trim().length === 0) {
        setSearchParams({})
        return
      }

      setSearchParams({ q: value.trim() })

      if (value.trim().length < 2) {
        return
      }

      setLoading(true)

      try {
        const results = await api.catalogue.search(value.trim())
        setProducts(results)
        setHasMore(false)
        setError(null)
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : 'Search failed.')
      } finally {
        setLoading(false)
      }
    },
    [setSearchParams],
  )

  // Sync with URL query parameter if present
  useEffect(() => {
    if (urlQuery && urlQuery !== query) {
      void runSearch(urlQuery)
    }
  }, [urlQuery, query, runSearch])

  // Fetch products by category when no search query
  useEffect(() => {
    if (query.trim().length > 0) {
      return
    }

    const slug = categorySlug ?? defaultCategoryOf(categories)

    if (slug === '') {
      setProducts([])
      setCursor(undefined)
      setHasMore(false)
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)

    api.catalogue
      .productsInCategory(slug)
      .then((page) => {
        setProducts(page.items)
        setCursor(page.nextCursor)
        setHasMore(page.hasMore)
        setError(null)
      })
      .catch((failure: Error) => setError(failure.message))
      .finally(() => setLoading(false))
  }, [categorySlug, categories, query])

  async function loadMore() {
    if (!cursor || !categorySlug) {
      return
    }

    const page = await api.catalogue.productsInCategory(categorySlug, cursor)
    setProducts((current) => [...current, ...page.items])
    setCursor(page.nextCursor)
    setHasMore(page.hasMore)
  }

  const activeCategory = useMemo(() => {
    return categories.find((c) => c.slug === categorySlug) || categories[0]
  }, [categories, categorySlug])

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Hero Promotional Banner */}
      {!query && (
        <section className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-r from-emerald-800 via-emerald-700 to-teal-800 p-4 text-white shadow-xl shadow-emerald-950/10 sm:p-10">
          <div className="pointer-events-none absolute -right-10 -top-10 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 right-32 h-64 w-64 rounded-full bg-teal-400/20 blur-3xl" />

          <div className="relative z-10 max-w-xl space-y-2.5 sm:space-y-4">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] sm:text-xs font-semibold text-emerald-100 backdrop-blur-md">
              <Sparkles className="h-3.5 w-3.5 text-amber-300" />
              <span>{isHindi ? 'रोज़ाना मार्ट • रोज़मर्रा की हर ज़रूरत' : 'RojanaMart • Daily Needs & Superstore'}</span>
            </div>

            <h1 className="font-heading text-xl font-black tracking-tight sm:text-4xl sm:leading-tight">
              {isHindi ? (
                <>घर और रसोई की हर ज़रूरत <span className="text-amber-300">15 मिनट में</span> आपके द्वार</>
              ) : (
                <>Daily Groceries, Essentials & More in <span className="text-amber-300">15 Minutes</span></>
              )}
            </h1>

            <p className="text-xs text-emerald-100/90 sm:text-sm leading-relaxed">
              {isHindi
                ? 'किराना, राशन, ताज़ा डेयरी, स्नैक्स, पेय पदार्थ, पर्सनल केयर और घरेलू ज़रूरत का हर सामान। सबसे किफ़ायती दाम और 100% शुद्धता की गारंटी।'
                : 'Pantry staples, dairy, beverages, snacks, packaged foods, personal care, and household essentials. Best wholesale prices with 100% quality guarantee.'}
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-1 text-[11px] sm:text-xs font-medium text-emerald-200">
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-300" />
                <span>{isHindi ? '₹499 से अधिक पर फ़्री डिलीवरी' : 'Zero Delivery Fee above ₹499'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-300" />
                <span>{isHindi ? 'सुपरफास्ट 15-मिनट डिलीवरी' : 'Express 15-Min Delivery'}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Search & Category Filter Section */}
      <section className="space-y-3 sm:space-y-4">
        {/* In-page search bar on desktop (mobile uses sticky header search) */}
        <div className="relative hidden sm:block">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400" />
          <input
            type="search"
            className="field h-12 pl-11 text-base shadow-sm"
            placeholder={
              isHindi
                ? 'दुकान में खोजें (उदा. आटा, दाल, तेल, दूध, मैगी, चिप्स, साबुन)...'
                : 'Search within shop (e.g. atta, dal, oil, milk, maggi, chips, soap)...'
            }
            value={query}
            onChange={(event) => void runSearch(event.target.value)}
          />
          {query && (
            <button
              type="button"
              onClick={() => void runSearch('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 rounded-full bg-stone-100 p-1 text-xs text-stone-500 hover:bg-stone-200"
            >
              {isHindi ? 'हटाएं' : 'Clear'}
            </button>
          )}
        </div>

        {/* Categories Bar */}
        {categories.length > 0 && query.trim().length === 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-bold uppercase tracking-wider text-stone-500">
                {isHindi ? 'श्रेणियां देखें' : 'Explore Categories'}
              </h2>
              {activeCategory && (
                <span className="text-xs text-stone-500 hidden sm:inline">
                  {isHindi ? 'श्रेणी: ' : 'Browsing: '}
                  <strong className="text-emerald-800">
                    {getLocalizedCategoryName(activeCategory.slug, activeCategory.name, isHindi)}
                  </strong>
                </span>
              )}
            </div>

            <nav className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-2 scrollbar-none sm:mx-0 sm:px-0" aria-label="Categories">
              {categories.map((category) => {
                const isSelected = category.slug === (categorySlug ?? categories[0]?.slug)
                const emoji = getCategoryEmoji(category.slug, category.name)
                const displayName = getLocalizedCategoryName(category.slug, category.name, isHindi)

                return (
                  <Link
                    key={category.id}
                    to={`/c/${category.slug}`}
                    className={`flex shrink-0 items-center gap-2 rounded-2xl border px-4 py-2.5 text-xs font-bold transition-all shadow-sm ${
                      isSelected
                        ? 'border-emerald-600 bg-emerald-700 text-white shadow-emerald-700/20 shadow-md'
                        : 'border-stone-200/90 bg-white text-stone-700 hover:border-emerald-300 hover:bg-emerald-50/40'
                    }`}
                  >
                    <span className="text-base leading-none">{emoji}</span>
                    <span>{displayName}</span>
                  </Link>
                )
              })}
            </nav>
          </div>
        )}
      </section>

      {/* Error state */}
      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-2xl bg-red-50 p-4 text-xs font-medium text-red-800">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading state skeleton */}
      {loading && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="card h-64 animate-pulse bg-stone-100 p-4">
              <div className="h-32 w-full rounded-xl bg-stone-200" />
              <div className="mt-3 h-4 w-3/4 rounded bg-stone-200" />
              <div className="mt-2 h-4 w-1/2 rounded bg-stone-200" />
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && products.length === 0 && (
        <div className="card my-8 p-10 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-stone-100 text-stone-400">
            <Search className="h-7 w-7" />
          </div>
          <h2 className="text-lg font-bold text-stone-800">
            {isHindi ? 'कोई सामान नहीं मिला' : 'No matching items found'}
          </h2>
          <p className="mx-auto mt-1 max-w-sm text-xs text-stone-500">
            {isHindi
              ? `"${query}" से मेल खाने वाला कोई उत्पाद नहीं मिला। कृपया स्पेलिंग जांचें या ऊपर दी गई श्रेणियां देखें।`
              : `We couldn't find anything matching "${query}". Try checking for spelling mistakes or explore our fresh categories above.`}
          </p>
          <button
            type="button"
            onClick={() => void runSearch('')}
            className="btn-primary mt-4"
          >
            {isHindi ? 'खोज रीसेट करें' : 'Reset Search'}
          </button>
        </div>
      )}

      {/* Product Grid */}
      {!loading && products.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-stone-900 sm:text-xl">
              {query
                ? (isHindi ? `"${query}" के खोज परिणाम` : `Search Results for "${query}"`)
                : (activeCategory ? getLocalizedCategoryName(activeCategory.slug, activeCategory.name, isHindi) : (isHindi ? 'सभी उत्पाद' : 'All Products'))}
            </h2>
            <span className="text-xs font-semibold text-stone-500">
              {products.length} {isHindi ? 'सामान उपलब्ध' : `${products.length === 1 ? 'item' : 'items'} available`}
            </span>
          </div>

          <ul className="grid grid-cols-2 gap-2.5 sm:gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4">
            {products.map((product) => {
              const hasDiscount = product.maxMrpPaise > product.fromPricePaise
              const discountPercent = hasDiscount
                ? Math.round(((product.maxMrpPaise - product.fromPricePaise) / product.maxMrpPaise) * 100)
                : 0

              return (
                <li
                  key={product.id}
                  className="card card-hover group flex flex-col justify-between overflow-hidden border-stone-200/80 bg-white"
                >
                  <Link to={`/p/${product.slug}`} className="block">
                    {/* Image Area */}
                    <div className="relative aspect-square w-full overflow-hidden bg-stone-100">
                      {product.primaryImageUrl ? (
                        <img
                          src={product.primaryImageUrl}
                          alt={product.name}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-tr from-stone-100 to-stone-200 text-stone-300">
                          <ShoppingBag className="h-10 w-10 stroke-[1.2]" />
                        </div>
                      )}

                      {/* Discount Badge */}
                      {discountPercent > 0 && (
                        <span className="absolute left-2.5 top-2.5 rounded-lg bg-emerald-600 px-2 py-0.5 text-[11px] font-black uppercase text-white shadow-sm">
                          {discountPercent}% OFF
                        </span>
                      )}

                      {/* Delivery Speed Pill */}
                      <span className="absolute bottom-2 left-2.5 inline-flex items-center gap-1 rounded-md bg-white/90 px-1.5 py-0.5 text-[10px] font-bold text-stone-700 backdrop-blur-sm shadow-sm">
                        <Clock className="h-3 w-3 text-emerald-600" />
                        {isHindi ? '15 मिनट' : '15 MINS'}
                      </span>
                    </div>

                    {/* Content Details */}
                    <div className="p-2.5 sm:p-4">
                      {product.brand && (
                        <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-emerald-800">
                          {product.brand}
                        </p>
                      )}

                      <h3 className="line-clamp-2 mt-0.5 text-xs font-bold text-stone-800 sm:text-sm group-hover:text-emerald-700">
                        {product.name}
                      </h3>

                      {product.requiresWeighing && (
                        <span className="mt-1 inline-block text-[10px] font-medium text-amber-800 bg-amber-50 rounded px-1.5 py-0.5">
                          {isHindi ? 'खुला राशन · पैकिंग पर तौला जाएगा' : 'Loose · Weighed at pack'}
                        </span>
                      )}

                      {/* Price row */}
                      <div className="mt-2 flex items-baseline gap-1.5 sm:gap-2">
                        <span className="money text-sm font-black text-stone-900 sm:text-base">
                          {formatMoney(product.fromPricePaise)}
                        </span>
                        {hasDiscount && (
                          <span className="money text-[11px] sm:text-xs text-stone-400 line-through">
                            {formatMoney(product.maxMrpPaise)}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>

                  {/* Add action */}
                  <div className="px-2.5 pb-2.5 sm:px-4 sm:pb-4">
                    <Link
                      to={`/p/${product.slug}`}
                      className="btn-secondary w-full py-1.5 text-xs font-bold hover:bg-emerald-600 hover:text-white hover:border-emerald-600"
                    >
                      {product.variantCount > 1
                        ? (isHindi ? 'पैक चुनें' : 'Choose Pack')
                        : (isHindi ? 'देखें व जोड़ें' : 'View & Add')}
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {/* Pagination Load More */}
      {hasMore && (
        <div className="pt-4 text-center">
          <button
            type="button"
            className="btn-secondary px-8 py-3 font-bold"
            onClick={() => void loadMore()}
          >
            {isHindi ? 'और सामान लोड करें' : 'Load More Products'}
          </button>
        </div>
      )}
    </div>
  )
}

function defaultCategoryOf(categories: CategoryTree[]): string {
  return categories[0]?.slug ?? ''
}
